import { datadogRum } from "@datadog/browser-rum";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import {
  toActiveEscalationSummaries,
  useEscalationContext,
  useEscalationNotifications,
  useEscalationSubscriptions,
  type ActiveEscalationSummary,
  type HumanMessage,
} from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { memo, useCallback, useEffect, useRef } from "react";
import { chatInstances } from "../hooks/use-ask-redo-chat";
import { useEscalationAuthor } from "./escalation-author-context";
import {
  humanMessageToUIMessage,
  mergeAdminMessages,
} from "./intercom-to-uimessage";

/**
 * The Ask Redo escalation **data layer**. Always mounted at the
 * layout level. Owns one SSE subscription per active escalation (a
 * merchant can have several chats escalated at once), drives the
 * per-escalation toasts when the conversation isn't on screen,
 * captures the human agent's name + avatar into
 * EscalationAuthorContext, and pushes live human replies into the
 * cached Chat instance keyed by each escalation's own triage thread.
 *
 * Renders nothing. The slide-out is purely a view onto this state.
 */
export const AskRedoEscalationWatcher = memo(
  function AskRedoEscalationWatcher() {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const user = useRequiredContext(UserContext);
    const escalationContext = useEscalationContext();
    const author = useEscalationAuthor();
    const { isOpen, open } = useAskRedoSlidePanel();

    // Ref-stable "currently displayed thread" (the floating setter,
    // clobbered on every history click) — used ONLY to decide whether
    // a toast is redundant, never to route messages. Routing uses each
    // escalation's own bound triageThreadId from the summary.
    const displayedThreadId = escalationContext.triageThreadId;
    const displayedThreadIdRef = useRef(displayedThreadId);
    useEffect(() => {
      displayedThreadIdRef.current = displayedThreadId;
    }, [displayedThreadId]);

    // Ref-stable panel-open state for the closed-panel toast branch in
    // the SSE handler. We don't re-subscribe on `isOpen` flips — the
    // SSE stream is bound to escalationId.
    const isOpenRef = useRef(isOpen);
    useEffect(() => {
      isOpenRef.current = isOpen;
    }, [isOpen]);

    // Refs the notification hook expects.
    const hasInitializedRef = useRef(false);
    const pendingThreadLoadInFlightRef = useRef(false);

    // When the toast is clicked, open the Ask Redo slide-out with
    // the right thread id. The slide-out's `useAskRedoThreadHistory`
    // hydrates from the panel options.
    const loadThreadIntoChat = useCallback(
      async (threadId: string) => {
        open({ kind: "thread", threadId });
      },
      [open],
    );

    // Register the "navigate to thread" callback on EscalationContext
    // so the escalate-to-human card's "Go to existing" button works.
    // Without this the button is permanently disabled (help-chat used
    // to register this; Ask Redo had no wiring).
    const setNavigateToThread = escalationContext.setNavigateToThread;
    useEffect(() => {
      setNavigateToThread((threadId: string) => {
        open({ kind: "thread", threadId });
      });
      return () => setNavigateToThread(null);
    }, [setNavigateToThread, open]);

    const { notifyClosedEscalationMessage } = useEscalationNotifications({
      isOpen,
      open,
      threads: [],
      escalationContext,
      merchantRpcClient,
      getThreads: merchantRpcClient.getAskRedoThreads,
      loadThreadIntoChat,
      userEmail: user.email,
      hasInitializedRef,
      pendingThreadLoadInFlightRef,
      // The watcher clears per-escalation toasts itself (below) so
      // opening chat A doesn't nuke chat B's toast.
      clearToastsOnOpen: false,
    });

    // Hydrate the plural active-escalation map on mount so the
    // per-escalation SSE subscriptions cover EVERY escalated chat,
    // not just whichever one the server ranks first.
    const { setActiveEscalations, upsertActiveEscalation } = escalationContext;
    useEffect(() => {
      let cancelled = false;
      void (async () => {
        try {
          const result = await merchantRpcClient.checkActiveEscalation({});
          if (cancelled) {
            return;
          }
          setActiveEscalations(
            toActiveEscalationSummaries(result.activeEscalations),
          );
        } catch (err) {
          console.error("[ask-redo] active escalation hydration failed", err);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [merchantRpcClient, setActiveEscalations]);

    // Keep the map in sync with the bound escalation: a newly created
    // (or freshly rebound) escalation joins the plural pipeline
    // without waiting for a refetch.
    const boundEscalation = escalationContext.escalation;
    const activeEscalations = escalationContext.activeEscalations;
    useEffect(() => {
      if (
        boundEscalation.escalationId &&
        boundEscalation.triageThreadId &&
        (boundEscalation.status === "waiting" ||
          boundEscalation.status === "human-active") &&
        !activeEscalations.has(boundEscalation.escalationId)
      ) {
        upsertActiveEscalation({
          escalationId: boundEscalation.escalationId,
          triageThreadId: boundEscalation.triageThreadId,
          status: boundEscalation.status,
          lastAdminMessageAt: boundEscalation.lastAdminMessageAt,
          lastReadAt: boundEscalation.lastReadAt,
        });
      }
    }, [boundEscalation, activeEscalations, upsertActiveEscalation]);

    // One SSE listener per active escalation. Each message routes to
    // the cached Chat instance keyed by ITS escalation's bound triage
    // thread — a reply on chat A can never land on (or be dropped in
    // favor of) chat B. The agent's name + avatar are captured into
    // EscalationAuthorContext only for the bound escalation, since
    // that context backs the currently displayed banner.
    const handleHumanMessage = useHandler(
      (summary: ActiveEscalationSummary, message: HumanMessage) => {
        if (
          summary.escalationId === escalationContext.escalation.escalationId
        ) {
          author.recordAuthor({
            name: message.authorName?.split(" ")[0] ?? null,
            avatarUrl: message.authorAvatarUrl ?? null,
          });
        }

        const triageThreadId =
          summary.triageThreadId || escalationContext.escalation.triageThreadId;
        if (triageThreadId) {
          const chat = chatInstances.get(triageThreadId);
          if (chat) {
            const ui = humanMessageToUIMessage(message);
            // The Chat class only exposes a `messages` getter/setter —
            // the `setMessages` updater lives on the useChat hook's
            // return value, NOT on the Chat instance. Use the setter
            // directly; ReactChatState's setter fires the subscribed
            // useSyncExternalStore callbacks so every mounted useChat
            // re-renders with the appended message.
            const prev = chat.messages;
            if (!prev.some((m) => m.id === ui.id)) {
              chat.messages = [...prev, ui];
            }
          }
        }

        // Toast unless THIS escalation's conversation is on screen:
        // either the slide-out is open on its thread, or the full-page
        // Ask Redo route shows it. A reply on a DIFFERENT escalated
        // chat still toasts even while the panel is open — that's the
        // whole point of the plural pipeline. Toast click routes
        // through `useEscalationNotifications`' pendingThreadToOpen
        // flow, which reopens the panel on the right thread.
        const isDisplayedInPanel =
          isOpenRef.current &&
          !!triageThreadId &&
          displayedThreadIdRef.current === triageThreadId;
        if (
          !isDisplayedInPanel &&
          !isViewingEscalationOnFullPage(triageThreadId ?? null)
        ) {
          notifyClosedEscalationMessage(
            message,
            triageThreadId
              ? { escalationId: summary.escalationId, triageThreadId }
              : undefined,
          );
        }
      },
    );
    // The SSE stream only carries future events; a reply sent during the
    // reconnect gap is recovered by re-fetching and merge-deduping admin replies.
    const handleReconnect = useHandler(
      async (summary: ActiveEscalationSummary) => {
        try {
          const result = await merchantRpcClient.getEscalationMessages({
            escalationId: summary.escalationId,
          });
          const triageThreadId =
            summary.triageThreadId ||
            escalationContext.escalation.triageThreadId;
          if (triageThreadId) {
            const chat = chatInstances.get(triageThreadId);
            if (chat) {
              const merged = mergeAdminMessages(chat.messages, result.messages);
              if (merged !== chat.messages) {
                chat.messages = merged;
              }
            }
          }

          const latestAdmin = [...result.messages]
            .reverse()
            .find((m) => m.authorName || m.authorAvatarUrl);
          if (!latestAdmin) {
            return;
          }
          const isBound =
            summary.escalationId === escalationContext.escalation.escalationId;
          if (isBound) {
            author.recordAuthor({
              name: latestAdmin.authorName?.split(" ")[0] ?? null,
              avatarUrl: latestAdmin.authorAvatarUrl ?? null,
            });
          }
          // An admin reply in the gap implies the handoff is live — promote
          // status the same way the live human-message event path does.
          const lastAdminMessageAt = Math.round(latestAdmin.createdAt * 1000);
          escalationContext.updateActiveEscalation(summary.escalationId, {
            status: "human-active",
            lastAdminMessageAt,
          });
          if (isBound) {
            escalationContext.setEscalation({
              status: "human-active",
              lastAdminMessageAt,
            });
          }
        } catch {
          // Best-effort; the live SSE stream will populate on the next reply.
        }
      },
    );
    // The watcher exhausted its reconnect budget: the live channel is dead
    // until reload. Make it observable (RUM) so it isn't a silent failure.
    const handleGiveUp = useHandler((summary: ActiveEscalationSummary) => {
      datadogRum.addError(
        new Error("Ask Redo escalation watcher gave up reconnecting"),
        {
          escalationId: summary.escalationId,
          triageThreadId: summary.triageThreadId,
        },
      );
      datadogRum.addAction("ask_redo_escalation_watcher_gave_up", {
        escalationId: summary.escalationId,
      });
      console.error(
        "Ask Redo escalation watcher gave up reconnecting",
        summary.escalationId,
      );
    });
    useEscalationSubscriptions(
      handleHumanMessage,
      undefined,
      handleReconnect,
      handleGiveUp,
    );

    // Cold-start backfill: on mount (or when the active escalation
    // changes) the SSE stream only carries FUTURE events. To populate
    // agentName/avatar from history (e.g., after a page refresh
    // mid-conversation), fetch escalation messages once and feed the
    // newest admin author through `recordAuthor`.
    const escalationId = escalationContext.escalation.escalationId;
    useEffect(() => {
      if (!escalationId) {
        return;
      }
      let cancelled = false;
      void (async () => {
        try {
          const result = await merchantRpcClient.getEscalationMessages({
            escalationId,
          });
          if (cancelled) {
            return;
          }
          // Newest admin message wins — that's the one most likely
          // to reflect the current assignee.
          const latestAdmin = [...result.messages]
            .reverse()
            .find((m) => m.authorName || m.authorAvatarUrl);
          if (latestAdmin) {
            author.recordAuthor({
              name: latestAdmin.authorName?.split(" ")[0] ?? null,
              avatarUrl: latestAdmin.authorAvatarUrl ?? null,
            });
          }
        } catch {
          // Backfill is best-effort; SSE will populate on next reply.
        }
      })();
      return () => {
        cancelled = true;
      };
      // `author.recordAuthor` is stable; intentionally not in deps to
      // avoid re-fetching on author updates.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [escalationId, merchantRpcClient]);

    // Clear agent info when no escalation is in context (closed +
    // cleared, or post "New Conversation"). Prevents an old agent's
    // name/avatar from bleeding into the next escalation.
    useEffect(() => {
      if (!escalationId) {
        author.clearAuthor();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [escalationId]);

    // Toast dismissal on viewing lives in the slide-out session — it
    // knows its displayed thread synchronously; clearing here raced it.
    return null;
  },
);

/**
 * True when the user is currently on the full-page Ask Redo route AND
 * the path's `:threadId` segment matches the escalation's bound
 * thread. In that case the conversation is already on screen — a
 * notification toast would just duplicate the message bubble that's
 * about to render via the same SSE event.
 *
 * Reads `window.location.pathname` directly rather than threading a
 * `useLocation` consumer down to the watcher: the watcher is a
 * pure side-effect node with no render output, and the SSE handler
 * runs outside the React commit cycle anyway — a stale subscription
 * to react-router state would be the more fragile choice.
 */
function isViewingEscalationOnFullPage(
  boundTriageThreadId: string | null,
): boolean {
  if (typeof window === "undefined" || !boundTriageThreadId) {
    return false;
  }
  // Matches:
  //   /ask-redo
  //   /ask-redo/<threadId>
  //   /stores/<teamId>/ask-redo
  //   /stores/<teamId>/ask-redo/<threadId>
  const match = window.location.pathname.match(
    /\/ask-redo(?:\/([^/?#]+))?(?:[/?#]|$)/,
  );
  if (!match) {
    return false;
  }
  const pathThreadId = match[1];
  // Only suppress when the path's thread id explicitly matches the
  // escalation's bound thread. The bare `/ask-redo` (no segment) is
  // the "new conversation" hero — a toast there IS useful because
  // the merchant isn't looking at the escalated transcript.
  return pathThreadId === boundTriageThreadId;
}
